import _ from 'lodash';
import * as queryStringHelpers from 'qs';
import { getValues } from 'redux-form';

import { isPartnerCustomer } from '@src/containers/Offers/offers/offersHelpers';

import * as constants from './productTypes.constants';
import { getSplitTestValue } from '../utils/selectors';

export function getRequestedProductType(state) {
    return state?.lendingAPI?.application?.data?.requested_products?.[0]
        ?.product_type;
}

export function getSuggestedProductType(state) {
    return state?.lendingAPI?.application?.data?.requested_products?.[0]
        ?.suggested_product?.product_key;
}

export function getRequestedProductsDuration(state) {
    return state?.lendingAPI?.application?.data?.requested_products?.[0]
        ?.duration?.amount;
}

export const isRBLApplication = (state) => {
    return (
        getRequestedProductType(state) ===
        constants.MERCHANT_CASH_ADVANCE_PRODUCT_TYPE
    );
};

export function getStateKey(state) {
    return state?.lendingAPI?.context?.data?.state_key;
}

export function getCustomerStatus(state) {
    return state?.lendingAPI?.customerStatus;
}

export const getCountryCode = (state) =>
    _.get(state, 'lendingAPI.context.data.country_code', '');

export const isIntroducer = (state) => {
    const queryParams = queryStringHelpers.parse(
        window.location.search.substring(1)
    );

    return (
        _.get(state, 'lendingAPI.profile.data.user_type') === 'introducer' ||
        _.get(state, 'lendingAPI.profile.data.impersonator_type') ===
            'introducer' ||
        queryParams.viewing_as === 'introducer'
    );
};

export const isIntroducerApplication = (state) => {
    return !!getIntroducerId(state);
};

export const getIntroducerId = (state) => {
    return _.get(state, 'lendingAPI.application.data.introducer.introducer_id');
};

export const getApplicationId = (state) => {
    return _.get(state, 'lendingAPI.application.data.application_id');
};

export const isIndicativeOfferIntroducer = (state) => {
    return _.get(state, 'ui.attributes.indicative_offer_flow') === true;
};

export const isImpersonating = (state) =>
    _.get(state, 'lendingAPI.context.data.impersonating') === true;

export const getApplicantEmail = (state) =>
    _.get(state, 'fetched.application.payload.people[0].email');

export const getRequestedAmount = (state) =>
    _.get(state, 'lendingAPI.application.data.requested_products[0].amount');

export const getSuggestedAmount = (state) =>
    _.get(
        state,
        'lendingAPI.application.data.requested_products[0].suggested_product.amount'
    );

export const getSplitTests = (state) =>
    _.get(state, 'lendingAPI.context.data.user_split_tests', []);

export const getCompanyType = (state) => {
    return _.get(
        state,
        'lendingAPI.customer_state.data.application.company.type'
    );
};

const hasAutomatedDecisionUK = (state) => {
    const autoDecisionMaxLoanAmountUK = getSplitTestValue(
        state,
        'max_automated_loan_size_increase'
    )
        ? 25000
        : 15000;

    const companyTypeFormValue = _.get(
        getValues(state.form.sign_up),
        'businessType.company.type'
    );
    let isLimitedCompany = ['llp', 'ltd'].includes(companyTypeFormValue);
    if (!companyTypeFormValue) {
        isLimitedCompany = [
            'limited_liability_partnership',
            'limited_liability_company',
            'llp',
            'ltd'
        ].includes(getCompanyType(state));
    }

    return (
        getRequestedAmount(state) <= autoDecisionMaxLoanAmountUK &&
        isLimitedCompany &&
        !isRBLApplication(state)
    );
};

// splitTestValue returns boolean
const hasAutomatedDecisionDE = (state) => {
    const hasAutoLendingSplitTestDE = getSplitTestValue(state, 'al_v1');

    return (
        hasAutoLendingSplitTestDE && getRequestedProductsDuration(state) <= 12
    );
};

export function hasAutomatedDecision(state) {
    if (getCountryCode(state) === 'de') {
        return hasAutomatedDecisionDE(state);
    }
    return hasAutomatedDecisionUK(state);
}

export const getOpenApplication = (state) => {
    const application = _.get(state, 'lendingAPI.application.data');
    if (!application || !application.status) {
        return null;
    }

    return _.get(application, 'requested_products[0]');
};

export const missingRequiredApplicationDetails = (state) => {
    const openApplication = getOpenApplication(state);
    return (
        !openApplication ||
        (!openApplication.amount && !isDirectUKCustomer(state))
    );
};

export const getFormalIndicativeOffer = (state) => {
    const formalIndicativeOffer = _.get(
        state,
        'lendingAPI.formal_indicative_offers.data.formal_indicative_offers[0]'
    );
    const interestRate = _.get(formalIndicativeOffer, 'interest_rates[0].rate');

    return {
        ...formalIndicativeOffer,
        rate: interestRate
    };
};

export const hasFormalIndicativeOffers = (state) => {
    const formalIndicativeOffers = _.get(
        state,
        'lendingAPI.formal_indicative_offers.data.formal_indicative_offers',
        []
    );

    return formalIndicativeOffers.length > 0;
};

export const getTrueLayerBanks = (state) => {
    return _.get(state, 'lendingAPI.context.data.open_banking.truelayer_banks');
};

export function isDirectUKCustomer(state) {
    return !isPartnerCustomer(state) && getCountryCode(state) === 'uk';
}
